export type CategoriaRadioVerificada = 'verificada' | 'destacada' | 'premium' | 'elite' | 'oficial';

export interface RadioVerificada {
  id: number;
  nombre: string;
  frecuencia: string;
  categoriaRadio: string;
  categoriaVerificacion: CategoriaRadioVerificada;
  ciudad: string;
  pais: string;
  locutorPrincipal: string;
  oyentes: number;
  seguidores: number;
  rating: number;
  estaEnVivo: boolean;
  descripcion: string;
  logoUrl: string;
  imagenUrl: string;
  fechaVerificacion: string;
  otorgadoPor: string;
  numeroInsignia: string;
  beneficiosActivos: string[];
  posicionRanking: number;
  enTendencia: boolean;
  prioridadBusqueda: number;
  tags: string[];
}

export interface BeneficioVerificacion {
  id: number;
  titulo: string;
  icono: string;
  descripcion: string;
  color: string;
  bgColor: string;
}

export interface UbicacionInsignia {
  id: number;
  nombre: string;
  icono: string;
  descripcion: string;
  visible: boolean;
}

export interface ReglaRadiosVerificadas {
  id: number;
  titulo: string;
  descripcion: string;
  icono: string;
  gradiente: string;
  colorBorde: string;
}

export interface StatsRadiosVerificadas {
  totalRadiosVerificadas: number;
  porCategoria: Record<CategoriaRadioVerificada, number>;
  totalOyentes: number;
  tasaAprobacion: string;
  ultimaVerificacion: string;
  categoriaTop: string;
}

export const statsRadiosVerificadas: StatsRadiosVerificadas = {
  totalRadiosVerificadas: 25,
  porCategoria: {
    verificada: 8,
    destacada: 6,
    premium: 5,
    elite: 3,
    oficial: 3,
  },
  totalOyentes: 184500,
  tasaAprobacion: '96.8%',
  ultimaVerificacion: '2026-06-18',
  categoriaTop: 'Elite',
};

export const categoriasVerificacion: { id: CategoriaRadioVerificada; label: string; icono: string; color: string; bg: string; borde: string; prioridad: number; descripcion: string }[] = [
  { id: 'verificada', label: 'Verificada', icono: 'ri-verified-badge-line', color: 'text-sky-600', bg: 'bg-sky-50', borde: 'border-sky-200', prioridad: 1, descripcion: 'Identidad confirmada. Radio auténtica que pasó la verificación básica de RFW.' },
  { id: 'destacada', label: 'Destacada', icono: 'ri-star-line', color: 'text-amber-600', bg: 'bg-amber-50', borde: 'border-amber-200', prioridad: 2, descripcion: 'Radio con alto rendimiento, comunidad activa y contenido de calidad destacada.' },
  { id: 'premium', label: 'Premium', icono: 'ri-vip-crown-line', color: 'text-rose-600', bg: 'bg-rose-50', borde: 'border-rose-200', prioridad: 3, descripcion: 'Lo mejor del ecosistema. Calidad de transmisión superior y audiencia masiva.' },
  { id: 'elite', label: 'Elite', icono: 'ri-sparkling-2-line', color: 'text-violet-600', bg: 'bg-violet-50', borde: 'border-violet-200', prioridad: 4, descripcion: 'Círculo exclusivo de excelencia radial. Trayectoria, innovación y liderazgo.' },
  { id: 'oficial', label: 'Oficial', icono: 'ri-shield-check-line', color: 'text-emerald-600', bg: 'bg-emerald-50', borde: 'border-emerald-200', prioridad: 5, descripcion: 'Radio oficial RFW. La insignia máxima. Otorgada solo por el Consejo RFW.' },
];

export const beneficiosVerificacion: BeneficioVerificacion[] = [
  { id: 1, titulo: 'Mayor Visibilidad', icono: 'ri-eye-line', descripcion: 'Tu radio aparece primero en búsquedas, exploración y recomendaciones. La insignia atrae la atención de nuevos oyentes en todas las superficies de la plataforma.', color: 'text-sky-600', bgColor: 'bg-sky-50' },
  { id: 2, titulo: 'Prioridad en Búsquedas', icono: 'ri-search-line', descripcion: 'Las radios verificadas y destacadas rankean más alto en los resultados de búsqueda. Cuanto mayor es la categoría, más arriba aparece.', color: 'text-amber-600', bgColor: 'bg-amber-50' },
  { id: 3, titulo: 'Prioridad en Tendencias', icono: 'ri-fire-line', descripcion: 'Las radios con insignia tienen un multiplicador en el algoritmo de tendencias. Una radio Elite genera tendencia con menos actividad que una no verificada.', color: 'text-rose-600', bgColor: 'bg-rose-50' },
  { id: 4, titulo: 'Prioridad en Rankings', icono: 'ri-trophy-line', descripcion: 'En los rankings RFW, las insignias otorgan puntos extra. Una radio Oficial tiene ventaja tangible sobre radios no verificadas con métricas similares.', color: 'text-violet-600', bgColor: 'bg-violet-50' },
  { id: 5, titulo: 'Prioridad en Landing', icono: 'ri-home-5-line', descripcion: 'Las radios verificadas aparecen en el carrusel especial de la landing page de RFW. Posiciones preferenciales que garantizan exposición a toda la comunidad.', color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
];

export const ubicacionesInsignia: UbicacionInsignia[] = [
  { id: 1, nombre: 'Radio', icono: 'ri-radio-line', descripcion: 'Visible en el perfil público de la radio, junto al nombre y la frecuencia.', visible: true },
  { id: 2, nombre: 'Posts', icono: 'ri-article-line', descripcion: 'Aparece en cada post, foto y video que la radio publica.', visible: true },
  { id: 3, nombre: 'Comentarios', icono: 'ri-chat-1-line', descripcion: 'La insignia se muestra junto al nombre en todas las cabinas y chats.', visible: true },
  { id: 4, nombre: 'Cabina', icono: 'ri-message-2-line', descripcion: 'Visible dentro de la cabina de la radio y en mensajes destacados.', visible: true },
  { id: 5, nombre: 'Perfil', icono: 'ri-user-3-line', descripcion: 'En el perfil social de la radio dentro de la comunidad RFW.', visible: true },
];

export const reglasRadiosVerificadas: ReglaRadiosVerificadas[] = [
  {
    id: 1,
    titulo: 'Solo SA Puede Otorgar Verificaciones',
    descripcion: 'Únicamente los Super Admins de RFW tienen la autoridad de otorgar, modificar o revocar cualquier categoría de verificación. Ningún moderador, administrador de radio ni usuario puede dar insignias. Cada insignia es una decisión informada, documentada y auditable.',
    icono: 'ri-admin-line',
    gradiente: 'from-rose-500 to-amber-500',
    colorBorde: 'border-rose-200',
  },
  {
    id: 2,
    titulo: 'Las 5 Categorías Tienen Visibilidad Escalonada',
    descripcion: 'Verificada recibe el badge azul básico. Destacada suma prioridad en búsquedas. Premium obtiene carrusel en landing y prioridad en rankings. Elite accede a tendencias aceleradas. Oficial es la insignia máxima con todos los beneficios activados.',
    icono: 'ri-stack-line',
    gradiente: 'from-emerald-500 to-teal-500',
    colorBorde: 'border-emerald-200',
  },
  {
    id: 3,
    titulo: 'La Insignia es Visible en 5 Ubicaciones',
    descripcion: 'Radio, Posts, Comentarios, Cabina y Perfil — la insignia de verificación sigue a la radio en todas sus interacciones. Donde está la radio, está su insignia. Esto construye confianza inmediata en cada punto de contacto con la comunidad.',
    icono: 'ri-eye-2-line',
    gradiente: 'from-violet-500 to-purple-500',
    colorBorde: 'border-violet-200',
  },
  {
    id: 4,
    titulo: 'Las Mejores Radios Merecen Mayor Visibilidad',
    descripcion: 'El sistema está diseñado para que las radios que cumplen con los estándares de calidad, mantienen comunidades activas y generan contenido valioso reciban más exposición. No es un privilegio — es un reconocimiento al mérito que impulsa a todo el ecosistema.',
    icono: 'ri-medal-line',
    gradiente: 'from-amber-500 to-orange-500',
    colorBorde: 'border-amber-200',
  },
];

export const radiosVerificadas: RadioVerificada[] = [
  // ═══════ OFICIAL (3) — Máxima insignia ═══════
  {
    id: 1,
    nombre: 'RFW Radio',
    frecuencia: '87.00 RFW',
    categoriaRadio: 'Oficial',
    categoriaVerificacion: 'oficial',
    ciudad: 'Global',
    pais: 'Plataforma RFW',
    locutorPrincipal: 'RFW Oficial',
    oyentes: 15820,
    seguidores: 245000,
    rating: 5.0,
    estaEnVivo: true,
    descripcion: 'La estación oficial de RadioFrecuenciaWeb. Contenido exclusivo, entrevistas, programas originales y lo mejor de la plataforma. La insignia Oficial representa el estándar máximo de calidad, autenticidad y servicio a la comunidad RFW.',
    logoUrl: 'https://readdy.ai/api/search-image?query=RFW%20Radio%20official%20logo%20design%2C%20circular%20modern%20emblem%20with%20radio%20waves%20and%20star%2C%20minimalist%20professional%20broadcast%20identity%2C%20warm%20amber%20and%20coral%20gradient%20colors%2C%20clean%20geometric%20shapes%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20style&width=200&height=200&seq=rfw-radio-logo&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20broadcast%20headquarters%20with%20modern%20control%20room%20panoramic%20view%2C%20warm%20amber%20studio%20lighting%2C%20multiple%20LED%20screens%20showing%20live%20metrics%20and%20world%20map%2C%20state-of-the-art%20audio%20equipment%2C%20sleek%20professional%20corporate%20environment%2C%20editorial%20architectural%20photography%20with%20clean%20lines%20and%20sophisticated%20atmosphere&width=1600&height=600&seq=rv-rfw-radio&orientation=landscape',
    fechaVerificacion: '2024-01-01',
    otorgadoPor: 'Consejo RFW',
    numeroInsignia: 'RFW-OFC-00001',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Tendencias', 'Prioridad Rankings', 'Prioridad Landing'],
    posicionRanking: 1,
    enTendencia: true,
    prioridadBusqueda: 100,
    tags: ['oficial', 'fundadora', '24/7', 'global'],
  },
  {
    id: 2,
    nombre: 'La Mega 107.3 FM',
    frecuencia: '107.30 RFW',
    categoriaRadio: 'Música Urbana',
    categoriaVerificacion: 'oficial',
    ciudad: 'Bogotá',
    pais: 'Colombia',
    locutorPrincipal: 'Ricky Navarro',
    oyentes: 14200,
    seguidores: 2840000,
    rating: 4.92,
    estaEnVivo: true,
    descripcion: 'La emisora líder en música urbana y entretenimiento en Colombia. Más de 2.8 millones de oyentes fieles, programas emblemáticos como El Despertador y la comunidad más activa de RFW. Insignia Oficial otorgada por trayectoria, audiencia y calidad consistente.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20station%20logo%20design%20bold%20typography%20warm%20golden%20energy%20waves%20abstract%20sound%20frequency%20pattern%20modern%20clean%20brand%20identity%2C%20vibrant%20urban%20style&width=200&height=200&seq=rv-lamega&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Modern%20urban%20radio%20station%20broadcast%20studio%20with%20vibrant%20warm%20lighting%2C%20professional%20microphone%20setup%2C%20acoustic%20panels%20with%20city%20skyline%20graphics%2C%20energetic%20atmosphere%2C%20hosts%20interacting%20with%20community%20through%20multiple%20screens%2C%20editorial%20photography%20with%20rich%20warm%20tones%20and%20dynamic%20composition&width=1600&height=600&seq=rv-lamega-cover&orientation=landscape',
    fechaVerificacion: '2025-03-15',
    otorgadoPor: 'Ricardo Montero',
    numeroInsignia: 'RFW-OFC-00002',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Tendencias', 'Prioridad Rankings', 'Prioridad Landing'],
    posicionRanking: 2,
    enTendencia: true,
    prioridadBusqueda: 98,
    tags: ['urbana', 'entretenimiento', 'premios', 'comunidad'],
  },
  {
    id: 3,
    nombre: 'Onda Cero 104.5 FM',
    frecuencia: '104.50 RFW',
    categoriaRadio: 'Noticias',
    categoriaVerificacion: 'oficial',
    ciudad: 'Medellín',
    pais: 'Colombia',
    locutorPrincipal: 'Diego Armando Paz',
    oyentes: 11800,
    seguidores: 2100000,
    rating: 4.87,
    estaEnVivo: true,
    descripcion: 'La referencia en noticias, deportes y análisis en América Latina. Periodismo de calidad, debates profundos y la mejor cobertura deportiva de la región. Carrusel Deportivo es el programa más escuchado de su categoría.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20logo%20sleek%20typography%20cool%20blue%20tones%20sound%20waves%20abstract%20circular%20design%20contemporary%20brand%20identity%20professional%20news%20broadcast%20style&width=200&height=200&seq=rv-ondacero&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Professional%20news%20radio%20broadcast%20studio%20with%20large%20digital%20screens%20displaying%20headlines%20and%20world%20maps%2C%20modern%20minimalist%20design%2C%20warm%20neutral%20lighting%2C%20serious%20yet%20approachable%20atmosphere%2C%20journalists%20at%20work%2C%20editorial%20photography%20with%20clean%20composition%20and%20authoritative%20mood&width=1600&height=600&seq=rv-ondacero-cover&orientation=landscape',
    fechaVerificacion: '2025-05-20',
    otorgadoPor: 'Ricardo Montero',
    numeroInsignia: 'RFW-OFC-00003',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Tendencias', 'Prioridad Rankings', 'Prioridad Landing'],
    posicionRanking: 3,
    enTendencia: true,
    prioridadBusqueda: 96,
    tags: ['noticias', 'deportes', 'análisis', 'periodismo'],
  },

  // ═══════ ELITE (3) — Excelencia radial ═══════
  {
    id: 4,
    nombre: 'Rock Total Radio',
    frecuencia: '87.01 RFW',
    categoriaRadio: 'Rock',
    categoriaVerificacion: 'elite',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    locutorPrincipal: 'Andrés Castillo',
    oyentes: 12800,
    seguidores: 1890000,
    rating: 4.91,
    estaEnVivo: true,
    descripcion: 'El hogar del rock en español. Con Andrés Castillo al frente de Distorsión Total, esta radio argentina es un fenómeno continental. Comunidad apasionada, entrevistas exclusivas y la mejor curaduría de rock clásico y contemporáneo.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%2C%20circular%20design%20black%20and%20red%20color%20scheme%2C%20modern%20rock%20broadcast%20emblem%2C%20energetic%20and%20bold%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=rv-rocktotal&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20rock%20radio%20station%20broadcast%20studio%20with%20electric%20guitars%20on%20wall%20and%20vintage%20vinyl%20collection%2C%20warm%20amber%20studio%20lighting%2C%20professional%20microphone%20setup%2C%20energetic%20rock%20atmosphere%2C%20industrial%20loft%20aesthetic%2C%20editorial%20photography%20with%20soft%20shadows%20and%20warm%20color%20tones&width=1600&height=600&seq=rv-rocktotal-cover&orientation=landscape',
    fechaVerificacion: '2025-08-10',
    otorgadoPor: 'Carolina Méndez',
    numeroInsignia: 'RFW-ELT-00001',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Tendencias', 'Prioridad Rankings', 'Prioridad Landing'],
    posicionRanking: 4,
    enTendencia: true,
    prioridadBusqueda: 94,
    tags: ['rock', 'argentina', 'comunidad', 'tendencia'],
  },
  {
    id: 5,
    nombre: 'La Kalle 96.3 FM',
    frecuencia: '96.30 RFW',
    categoriaRadio: 'Música Urbana',
    categoriaVerificacion: 'elite',
    ciudad: 'Barranquilla',
    pais: 'Colombia',
    locutorPrincipal: 'La Flaca Hernández',
    oyentes: 9600,
    seguidores: 1600000,
    rating: 4.78,
    estaEnVivo: true,
    descripcion: 'La calle habla por La Kalle. Reggaetón, hip hop, trap y la cultura urbana más auténtica del Caribe colombiano. El Mañanero Kalle es el morning show más prendido de la costa. Autenticidad y flow inigualables.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20urban%20radio%20station%20logo%20street%20style%20typography%20graffiti%20inspired%20design%20hip%20hop%20culture%20sound%20wave%20modern%20edgy%20brand%20identity%2C%20bold%20Caribbean%20style&width=200&height=200&seq=rv-lakalle&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20Caribbean%20urban%20radio%20studio%20with%20colorful%20street%20art%20murals%2C%20warm%20tropical%20lighting%2C%20DJ%20turntables%20and%20professional%20microphones%2C%20reggaeton%20and%20hip%20hop%20culture%20aesthetic%2C%20lively%20energetic%20broadcast%20atmosphere%2C%20editorial%20photography%20with%20saturated%20warm%20colors&width=1600&height=600&seq=rv-lakalle-cover&orientation=landscape',
    fechaVerificacion: '2025-01-28',
    otorgadoPor: 'Ricardo Montero',
    numeroInsignia: 'RFW-ELT-00002',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Tendencias', 'Prioridad Rankings', 'Prioridad Landing'],
    posicionRanking: 5,
    enTendencia: true,
    prioridadBusqueda: 92,
    tags: ['urbana', 'reggaetón', 'caribe', 'auténtica'],
  },
  {
    id: 6,
    nombre: 'Radio Salsa Brava',
    frecuencia: '110.70 RFW',
    categoriaRadio: 'Salsa',
    categoriaVerificacion: 'elite',
    ciudad: 'San Juan',
    pais: 'Puerto Rico',
    locutorPrincipal: 'Carlos Vega',
    oyentes: 10550,
    seguidores: 1450000,
    rating: 4.85,
    estaEnVivo: true,
    descripcion: 'La mejor salsa, timba y son cubano. Ritmo latino las 24 horas del día desde Puerto Rico. Carlos "El Sonero" Vega lidera con carisma, conocimiento musical y una comunidad que baila unida.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Salsa%20music%20radio%20station%20logo%20with%20congas%20and%20tropical%20palm%20silhouette%2C%20circular%20design%20vibrant%20orange%20and%20yellow%20colors%2C%20festive%20Caribbean%20broadcast%20emblem%2C%20energetic%20Latin%20rhythm%20feel&width=200&height=200&seq=rv-salsabrava&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Tropical%20salsa%20radio%20studio%20with%20conga%20drums%20and%20colorful%20Caribbean%20decorations%2C%20warm%20sunset%20golden%20lighting%2C%20Latin%20music%20posters%20on%20wall%2C%20vibrant%20festive%20atmosphere%2C%20professional%20broadcast%20equipment%2C%20editorial%20photography%20with%20rich%20warm%20tones&width=1600&height=600&seq=rv-salsabrava-cover&orientation=landscape',
    fechaVerificacion: '2025-09-05',
    otorgadoPor: 'Carolina Méndez',
    numeroInsignia: 'RFW-ELT-00003',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Tendencias', 'Prioridad Rankings', 'Prioridad Landing'],
    posicionRanking: 6,
    enTendencia: false,
    prioridadBusqueda: 90,
    tags: ['salsa', 'puerto rico', 'caribe', 'baile'],
  },

  // ═══════ PREMIUM (5) — Calidad superior ═══════
  {
    id: 7,
    nombre: 'Electro Vibes FM',
    frecuencia: '96.50 RFW',
    categoriaRadio: 'Electrónica',
    categoriaVerificacion: 'premium',
    ciudad: 'Miami',
    pais: 'Estados Unidos',
    locutorPrincipal: 'DJ Spark',
    oyentes: 9400,
    seguidores: 1120000,
    rating: 4.72,
    estaEnVivo: true,
    descripcion: 'House, techno y electrónica 24/7 desde Miami. DJ Spark mezcla en vivo con los mejores beats del momento. Bass Drop Session es el programa nocturno más escuchado del sur de Florida.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Electronic%20music%20radio%20station%20logo%20with%20synthesizer%20waveform%20and%20neon%20accents%2C%20circular%20design%20vibrant%20cyan%20and%20magenta%20colors%2C%20futuristic%20broadcast%20emblem%2C%20energetic%20club%20music%20feel&width=200&height=200&seq=rv-electrovibes&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Futuristic%20electronic%20music%20radio%20studio%20with%20neon%20cyan%20and%20magenta%20lighting%2C%20DJ%20mixing%20console%20with%20glowing%20knobs%2C%20synthesizer%20equipment%2C%20modern%20minimalist%20aesthetic%2C%20professional%20broadcast%20atmosphere%2C%20editorial%20photography%20with%20cyberpunk-inspired%20mood%20lighting&width=1600&height=600&seq=rv-electrovibes-cover&orientation=landscape',
    fechaVerificacion: '2025-04-15',
    otorgadoPor: 'Ricardo Montero',
    numeroInsignia: 'RFW-PRM-00001',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Tendencias', 'Prioridad Rankings'],
    posicionRanking: 7,
    enTendencia: true,
    prioridadBusqueda: 85,
    tags: ['electrónica', 'miami', 'dj', 'nocturna'],
  },
  {
    id: 8,
    nombre: 'Radio Hip Hop Nation',
    frecuencia: '170.40 RFW',
    categoriaRadio: 'Hip Hop',
    categoriaVerificacion: 'premium',
    ciudad: 'Atlanta',
    pais: 'Estados Unidos',
    locutorPrincipal: 'Big Mike',
    oyentes: 14200,
    seguidores: 1350000,
    rating: 4.68,
    estaEnVivo: true,
    descripcion: 'Hip hop, rap y cultura urbana desde Atlanta. Big Mike y DJ Scratch lideran con los beats más duros. Mic Check Live es el programa de hip hop más interactivo de la plataforma.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Hip%20hop%20radio%20station%20logo%20with%20turntable%20and%20graffiti%20style%20crown%2C%20circular%20design%20bold%20black%20and%20gold%20colors%2C%20urban%20broadcast%20emblem%2C%20street%20music%20culture%20feel&width=200&height=200&seq=rv-hiphopnation&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Urban%20hip%20hop%20radio%20studio%20with%20graffiti%20art%20on%20exposed%20brick%20walls%2C%20turntables%20and%20professional%20microphones%2C%20purple%20and%20gold%20mood%20lighting%2C%20street%20culture%20vibe%2C%20dynamic%20broadcast%20atmosphere%2C%20editorial%20photography%20with%20dramatic%20urban%20mood&width=1600&height=600&seq=rv-hiphopnation-cover&orientation=landscape',
    fechaVerificacion: '2025-06-20',
    otorgadoPor: 'Ricardo Montero',
    numeroInsignia: 'RFW-PRM-00002',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Tendencias', 'Prioridad Rankings'],
    posicionRanking: 8,
    enTendencia: true,
    prioridadBusqueda: 83,
    tags: ['hip hop', 'atlanta', 'rap', 'batallas'],
  },
  {
    id: 9,
    nombre: 'La Mexicana FM',
    frecuencia: '103.50 RFW',
    categoriaRadio: 'Regional Mexicana',
    categoriaVerificacion: 'premium',
    ciudad: 'Houston',
    pais: 'Estados Unidos',
    locutorPrincipal: 'Don Fernando Ramírez',
    oyentes: 12450,
    seguidores: 1280000,
    rating: 4.75,
    estaEnVivo: true,
    descripcion: 'El sonido auténtico de México directo a tus oídos. Banda, norteño y más. Don Fernando Ramírez es una institución de la radio regional con décadas de trayectoria.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20with%20guitar%20and%20sombrero%20elements%2C%20circular%20design%20warm%20orange%20and%20green%20colors%2C%20clean%20modern%20broadcast%20style%2C%20festive%20vibrant%20feel&width=200&height=200&seq=rv-lamexicana&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20studio%20with%20colorful%20traditional%20decorations%20and%20instruments%2C%20warm%20festive%20lighting%2C%20professional%20broadcast%20setup%2C%20cultural%20atmosphere%2C%20editorial%20photography%20with%20vibrant%20warm%20colors%20and%20rich%20textures&width=1600&height=600&seq=rv-lamexicana-cover&orientation=landscape',
    fechaVerificacion: '2025-02-10',
    otorgadoPor: 'Ricardo Montero',
    numeroInsignia: 'RFW-PRM-00003',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Rankings'],
    posicionRanking: 9,
    enTendencia: true,
    prioridadBusqueda: 81,
    tags: ['regional mexicana', 'houston', 'banda', 'tradición'],
  },
  {
    id: 10,
    nombre: 'Radio Nueva Actitud',
    frecuencia: '94.70 RFW',
    categoriaRadio: 'Pop',
    categoriaVerificacion: 'premium',
    ciudad: 'Ciudad de México',
    pais: 'México',
    locutorPrincipal: 'Valentina Ríos',
    oyentes: 6500,
    seguidores: 980000,
    rating: 4.65,
    estaEnVivo: true,
    descripcion: 'La mejor música pop y entretenimiento en vivo desde el corazón de México. Valentina Ríos revolucionó el formato juvenil con Tardes con Vale, el programa con mayor interacción en redes de toda la plataforma.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20radio%20station%20logo%20design%20with%20musical%20notes%20and%20frequency%20waves%2C%20clean%20circular%20emblem%20style%2C%20warm%20golden%20and%20white%20color%20palette%2C%20simple%20geometric%20shapes%2C%20professional%20broadcast%20aesthetic&width=200&height=200&seq=rv-nuevaactitud&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20modern%20pop%20radio%20studio%20in%20Mexico%20City%20with%20colorful%20neon%20accents%2C%20large%20windows%20with%20natural%20daylight%2C%20professional%20microphones%20and%20mixing%20console%2C%20youthful%20energetic%20atmosphere%2C%20editorial%20photography%20with%20clean%20modern%20aesthetic%20and%20warm%20natural%20tones&width=1600&height=600&seq=rv-nuevaactitud-cover&orientation=landscape',
    fechaVerificacion: '2025-07-01',
    otorgadoPor: 'Carolina Méndez',
    numeroInsignia: 'RFW-PRM-00004',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Rankings'],
    posicionRanking: 10,
    enTendencia: true,
    prioridadBusqueda: 79,
    tags: ['pop', 'méxico', 'juvenil', 'interacción'],
  },
  {
    id: 11,
    nombre: 'Deportes Total Radio',
    frecuencia: '120.00 RFW',
    categoriaRadio: 'Deportes',
    categoriaVerificacion: 'premium',
    ciudad: 'Dallas',
    pais: 'Estados Unidos',
    locutorPrincipal: 'Javier Solís',
    oyentes: 10890,
    seguidores: 1150000,
    rating: 4.82,
    estaEnVivo: true,
    descripcion: 'La cobertura deportiva más completa. Fútbol, béisbol, baloncesto y más con el análisis táctico de primer nivel de Javier Solís. La Final del Campeonato Nacional fue el evento más comentado del año.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%2C%20circular%20design%20dark%20navy%20and%20gold%20color%20scheme%2C%20dynamic%20broadcast%20emblem%2C%20energetic%20sports%20feel&width=200&height=200&seq=rv-deportestotal&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Professional%20sports%20radio%20studio%20with%20multiple%20large%20screens%20showing%20live%20matches%2C%20trophy%20cabinet%20on%20display%2C%20dynamic%20broadcast%20atmosphere%2C%20professional%20sports%20lighting%2C%20modern%20clean%20production%20environment%2C%20editorial%20photography%20with%20dramatic%20energy&width=1600&height=600&seq=rv-deportestotal-cover&orientation=landscape',
    fechaVerificacion: '2025-03-20',
    otorgadoPor: 'Ricardo Montero',
    numeroInsignia: 'RFW-PRM-00005',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Rankings'],
    posicionRanking: 11,
    enTendencia: false,
    prioridadBusqueda: 77,
    tags: ['deportes', 'dallas', 'fútbol', 'análisis'],
  },

  // ═══════ DESTACADA (6) — Alto rendimiento ═══════
  {
    id: 12,
    nombre: 'Tropicalísima 102.7 FM',
    frecuencia: '102.70 RFW',
    categoriaRadio: 'Música Tropical',
    categoriaVerificacion: 'destacada',
    ciudad: 'Cali',
    pais: 'Colombia',
    locutorPrincipal: 'Valentina Orozco',
    oyentes: 8200,
    seguidores: 750000,
    rating: 4.58,
    estaEnVivo: true,
    descripcion: 'La emisora que pone a bailar a todo el Pacífico colombiano. Salsa, merengue, bachata y los ritmos tropicales más pegados. Valentina Orozco lidera Tardes con Valentina con carisma inigualable.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20tropical%20radio%20station%20logo%20warm%20sunset%20colors%20palm%20tree%20silhouette%20sound%20waves%20rhythmic%20pattern%20festive%20modern%20brand%20identity%2C%20Colombian%20Pacific%20style&width=200&height=200&seq=rv-tropicalisima&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Tropical%20radio%20broadcast%20studio%20in%20Cali%20Colombia%20with%20warm%20sunset%20lighting%2C%20colorful%20Latin%20decorations%2C%20professional%20audio%20equipment%2C%20festive%20and%20welcoming%20atmosphere%2C%20large%20windows%20with%20tropical%20city%20view%2C%20editorial%20photography%20with%20saturated%20warm%20tones&width=1600&height=600&seq=rv-tropicalisima-cover&orientation=landscape',
    fechaVerificacion: '2025-08-22',
    otorgadoPor: 'Carolina Méndez',
    numeroInsignia: 'RFW-DST-00001',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Landing'],
    posicionRanking: 12,
    enTendencia: false,
    prioridadBusqueda: 72,
    tags: ['tropical', 'cali', 'salsa', 'bachata'],
  },
  {
    id: 13,
    nombre: 'Radio Country Road',
    frecuencia: '190.20 RFW',
    categoriaRadio: 'Country',
    categoriaVerificacion: 'destacada',
    ciudad: 'Nashville',
    pais: 'Estados Unidos',
    locutorPrincipal: 'Billy Ray Thompson',
    oyentes: 7230,
    seguidores: 620000,
    rating: 4.55,
    estaEnVivo: true,
    descripcion: 'Country clásico y moderno desde Nashville, Tennessee. Billy Ray Thompson trae lo mejor del country americano con entrevistas a las estrellas del Grand Ole Opry.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Country%20music%20radio%20station%20logo%20with%20acoustic%20guitar%20and%20cowboy%20hat%2C%20circular%20design%20warm%20brown%20and%20cream%20colors%2C%20rustic%20broadcast%20emblem%2C%20authentic%20Nashville%20country%20feel&width=200&height=200&seq=rv-countryroad&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Rustic%20country%20radio%20studio%20in%20Nashville%20with%20vintage%20acoustic%20guitars%20on%20wooden%20walls%2C%20warm%20amber%20lighting%2C%20professional%20broadcast%20desk%2C%20cozy%20authentic%20atmosphere%2C%20framed%20country%20music%20memorabilia%2C%20editorial%20photography%20with%20warm%20natural%20tones&width=1600&height=600&seq=rv-countryroad-cover&orientation=landscape',
    fechaVerificacion: '2025-10-15',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-DST-00002',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Landing'],
    posicionRanking: 13,
    enTendencia: false,
    prioridadBusqueda: 70,
    tags: ['country', 'nashville', 'americana', 'guitarra'],
  },
  {
    id: 14,
    nombre: 'Rock & Pop 95.1 FM',
    frecuencia: '95.10 RFW',
    categoriaRadio: 'Rock',
    categoriaVerificacion: 'destacada',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    locutorPrincipal: 'Ricky Navarro',
    oyentes: 6700,
    seguidores: 890000,
    rating: 4.65,
    estaEnVivo: true,
    descripcion: 'El hogar del rock en español y el pop alternativo. Noches de Rock con Ricky Navarro es cita obligada para los amantes de las guitarras. Irreverencia, buena música y una comunidad que respira rock.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Bold%20rock%20radio%20station%20logo%20edgy%20typography%20electric%20guitar%20motif%20sound%20wave%20graphic%20dark%20background%20contrast%20modern%20brand%20identity%2C%20Argentine%20rock%20style&width=200&height=200&seq=rv-rockpop&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20studio%20in%20Buenos%20Aires%20with%20vintage%20concert%20posters%20on%20exposed%20brick%20walls%2C%20electric%20guitars%20on%20display%2C%20warm%20moody%20red%20lighting%2C%20professional%20broadcast%20desk%2C%20passionate%20rock%20atmosphere%2C%20editorial%20photography%20with%20dramatic%20shadows&width=1600&height=600&seq=rv-rockpop-cover&orientation=landscape',
    fechaVerificacion: '2025-07-10',
    otorgadoPor: 'Carolina Méndez',
    numeroInsignia: 'RFW-DST-00003',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Landing'],
    posicionRanking: 14,
    enTendencia: false,
    prioridadBusqueda: 68,
    tags: ['rock', 'argentina', 'alternativo', 'entrevistas'],
  },
  {
    id: 15,
    nombre: 'Radio Reggae Vibes',
    frecuencia: '150.30 RFW',
    categoriaRadio: 'Reggae',
    categoriaVerificacion: 'destacada',
    ciudad: 'Kingston',
    pais: 'Jamaica',
    locutorPrincipal: 'Rasta Mike',
    oyentes: 5670,
    seguidores: 480000,
    rating: 4.52,
    estaEnVivo: true,
    descripcion: 'Reggae, dancehall y ritmos caribeños desde Jamaica. Buenas vibras, conciencia social y la música que cambió el mundo. Rasta Mike transmite desde el corazón de Kingston.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Reggae%20radio%20station%20logo%20with%20lion%20of%20Judah%20and%20palm%20tree%2C%20circular%20design%20vibrant%20green%20yellow%20and%20red%20colors%2C%20authentic%20Jamaican%20broadcast%20emblem%2C%20chilled%20island%20music%20feel&width=200&height=200&seq=rv-reggaevibes&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Beachside%20reggae%20radio%20studio%20in%20Jamaica%20with%20bamboo%20decor%20and%20tropical%20plants%2C%20natural%20daylight%20streaming%20through%20open%20windows%2C%20ocean%20view%20background%2C%20relaxed%20island%20atmosphere%2C%20professional%20broadcast%20equipment%2C%20editorial%20photography%20with%20bright%20Caribbean%20natural%20light&width=1600&height=600&seq=rv-reggaevibes-cover&orientation=landscape',
    fechaVerificacion: '2025-11-05',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-DST-00004',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Landing'],
    posicionRanking: 15,
    enTendencia: false,
    prioridadBusqueda: 66,
    tags: ['reggae', 'jamaica', 'dancehall', 'caribe'],
  },
  {
    id: 16,
    nombre: 'Radio Fe y Esperanza',
    frecuencia: '87.03 RFW',
    categoriaRadio: 'Cristiana',
    categoriaVerificacion: 'destacada',
    ciudad: 'Miami',
    pais: 'Estados Unidos',
    locutorPrincipal: 'Pastora María Elena',
    oyentes: 5632,
    seguidores: 420000,
    rating: 4.72,
    estaEnVivo: true,
    descripcion: 'Transmitiendo esperanza y fe para toda la comunidad cristiana de habla hispana. Música de adoración, mensajes inspiradores y una comunidad unida por la fe.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Christian%20radio%20station%20logo%20with%20dove%20symbol%20and%20cross%2C%20circular%20minimalist%20design%2C%20soft%20warm%20colors%20white%20and%20gentle%20golden%20tones%2C%20clean%20modern%20broadcast%20emblem%2C%20spiritual%20community%20radio&width=200&height=200&seq=rv-fe-esperanza&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Warm%20peaceful%20Christian%20radio%20studio%20with%20natural%20morning%20light%2C%20simple%20elegant%20wooden%20desk%20with%20professional%20microphone%2C%20inspirational%20decor%20with%20subtle%20cross%20motifs%2C%20calm%20spiritual%20atmosphere%2C%20editorial%20photography%20with%20soft%20natural%20lighting&width=1600&height=600&seq=rv-fe-esperanza-cover&orientation=landscape',
    fechaVerificacion: '2025-09-30',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-DST-00005',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas', 'Prioridad Landing'],
    posicionRanking: 16,
    enTendencia: false,
    prioridadBusqueda: 64,
    tags: ['cristiana', 'miami', 'fe', 'adoración'],
  },
  {
    id: 17,
    nombre: 'Jazz Nocturno Radio',
    frecuencia: '91.30 RFW',
    categoriaRadio: 'Jazz',
    categoriaVerificacion: 'destacada',
    ciudad: 'Nueva Orleans',
    pais: 'Estados Unidos',
    locutorPrincipal: 'Robert Johnson',
    oyentes: 3456,
    seguidores: 310000,
    rating: 4.68,
    estaEnVivo: true,
    descripcion: 'El mejor jazz clásico y contemporáneo desde Nueva Orleans. Robert "Blue" Johnson trae la esencia del jazz con curaduría exquisita y sesiones en vivo desde el French Quarter.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Jazz%20radio%20station%20logo%20with%20saxophone%20and%20crescent%20moon%2C%20circular%20design%20deep%20amber%20and%20warm%20gold%20colors%2C%20smooth%20sophisticated%20broadcast%20emblem%2C%20classic%20New%20Orleans%20jazz%20feel&width=200&height=200&seq=rv-jazznocturno&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Intimate%20jazz%20radio%20studio%20in%20New%20Orleans%20with%20warm%20amber%20lighting%2C%20saxophone%20on%20display%2C%20vintage%20microphone%2C%20brick%20wall%20background%2C%20sophisticated%20moody%20atmosphere%2C%20French%20Quarter%20aesthetic%2C%20editorial%20photography%20with%20rich%20warm%20shadows&width=1600&height=600&seq=rv-jazznocturno-cover&orientation=landscape',
    fechaVerificacion: '2025-12-01',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-DST-00006',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas'],
    posicionRanking: 17,
    enTendencia: false,
    prioridadBusqueda: 62,
    tags: ['jazz', 'nueva orleans', 'saxofón', 'clásico'],
  },

  // ═══════ VERIFICADA (8) — Identidad confirmada ═══════
  {
    id: 18,
    nombre: 'Radio Felicidad 88.9 FM',
    frecuencia: '88.90 RFW',
    categoriaRadio: 'Música Variada',
    categoriaVerificacion: 'verificada',
    ciudad: 'Lima',
    pais: 'Perú',
    locutorPrincipal: 'Laura García',
    oyentes: 4800,
    seguidores: 480000,
    rating: 4.45,
    estaEnVivo: true,
    descripcion: 'Música para el alma y contenido positivo. Laura García conduce El Show de la Mañana con calidez y alegría que alegran las mañanas limeñas. La radio que acompaña con buena energía.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Warm%20welcoming%20radio%20station%20logo%20soft%20golden%20tones%20heart%20motif%20gentle%20sound%20wave%20design%20comforting%20community%20feel%20modern%20brand%20identity%2C%20Lima%20Peru%20style&width=200&height=200&seq=rv-felicidad&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Cozy%20welcoming%20radio%20studio%20in%20Lima%20Peru%20with%20warm%20natural%20lighting%2C%20plants%20and%20comfortable%20seating%2C%20professional%20broadcast%20equipment%2C%20family-friendly%20atmosphere%2C%20large%20windows%20with%20city%20view%2C%20editorial%20photography%20with%20soft%20warm%20tones&width=1600&height=600&seq=rv-felicidad-cover&orientation=landscape',
    fechaVerificacion: '2025-10-05',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-VRF-00001',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas'],
    posicionRanking: 18,
    enTendencia: false,
    prioridadBusqueda: 55,
    tags: ['variada', 'lima', 'bienestar', 'familiar'],
  },
  {
    id: 19,
    nombre: 'Radio Cultura Digital',
    frecuencia: '91.10 RFW',
    categoriaRadio: 'Cultura',
    categoriaVerificacion: 'verificada',
    ciudad: 'Lima',
    pais: 'Perú',
    locutorPrincipal: 'Lucía Fernández',
    oyentes: 3100,
    seguidores: 350000,
    rating: 4.55,
    estaEnVivo: false,
    descripcion: 'Contenido cultural de excelencia. Divulgación artística, música clásica y programación educativa. Lucía Fernández eleva la radio cultural con Clásicos del Mundo, el espacio más premiado de Perú.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Cultural%20radio%20station%20logo%20design%2C%20elegant%20refined%20broadcast%20identity%2C%20warm%20terracotta%20and%20gold%20tones%2C%20classic%20emblem%20with%20frequency%2C%20artistic%20brush%20motif%2C%20premium%20brand%20identity%20design&width=200&height=200&seq=rv-culturadigital&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Elegant%20cultural%20radio%20studio%20with%20floor-to-ceiling%20bookshelves%2C%20grand%20piano%20in%20background%2C%20warm%20sophisticated%20lighting%2C%20classical%20music%20atmosphere%2C%20refined%20academic%20aesthetic%2C%20editorial%20photography%20with%20golden%20hour%20warmth&width=1600&height=600&seq=rv-culturadigital-cover&orientation=landscape',
    fechaVerificacion: '2025-11-15',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-VRF-00002',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas'],
    posicionRanking: 19,
    enTendencia: false,
    prioridadBusqueda: 53,
    tags: ['cultura', 'lima', 'clásica', 'educativa'],
  },
  {
    id: 20,
    nombre: 'Radio Élite Digital',
    frecuencia: '99.10 RFW',
    categoriaRadio: 'Tecnología',
    categoriaVerificacion: 'verificada',
    ciudad: 'Bogotá',
    pais: 'Colombia',
    locutorPrincipal: 'Diego Quintero',
    oyentes: 4200,
    seguidores: 420000,
    rating: 4.48,
    estaEnVivo: true,
    descripcion: 'Innovación en formatos digitales. Radio pionera en formatos híbridos radio-podcast con Diego Quintero, el creador de contenido tech más seguido de Latinoamérica.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Modern%20digital%20radio%20station%20logo%20design%2C%20sleek%20minimalist%20broadcast%20identity%2C%20warm%20violet%20and%20silver%20tones%2C%20clean%20circular%20emblem%2C%20pixel-inspired%20geometric%20motif%2C%20premium%20brand%20identity%20design&width=200&height=200&seq=rv-elitedigital&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Futuristic%20digital%20radio%20studio%20with%20multiple%20monitors%20showing%20code%20and%20analytics%2C%20RGB%20ambient%20lighting%2C%20modern%20minimalist%20desk%20setup%2C%20podcast%20equipment%2C%20tech%20startup%20vibe%2C%20editorial%20photography%20with%20cyberpunk%20aesthetic%20and%20clean%20lines&width=1600&height=600&seq=rv-elitedigital-cover&orientation=landscape',
    fechaVerificacion: '2025-12-15',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-VRF-00003',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas'],
    posicionRanking: 20,
    enTendencia: false,
    prioridadBusqueda: 51,
    tags: ['tecnología', 'podcast', 'digital', 'innovación'],
  },
  {
    id: 21,
    nombre: 'Radio Industria',
    frecuencia: '93.50 RFW',
    categoriaRadio: 'Noticias',
    categoriaVerificacion: 'verificada',
    ciudad: 'Santiago',
    pais: 'Chile',
    locutorPrincipal: 'Gabriela Sotomayor',
    oyentes: 5100,
    seguidores: 380000,
    rating: 4.52,
    estaEnVivo: true,
    descripcion: 'Rigor periodístico comprobado. Gabriela Sotomayor lidera el noticiero más respetado de Chile con un equipo de investigación galardonado. Premio Nacional de Periodismo.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Professional%20news%20radio%20station%20logo%20design%2C%20serious%20trustworthy%20broadcast%20identity%2C%20navy%20and%20cream%20tones%2C%20clean%20emblem%20with%20frequency%2C%20newspaper-inspired%20typography%2C%20premium%20brand%20identity%20design&width=200&height=200&seq=rv-industria&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Professional%20newsroom%20radio%20studio%20in%20Santiago%20Chile%20with%20large%20digital%20screens%20showing%20headlines%2C%20clean%20modern%20aesthetic%2C%20journalists%20at%20workstations%2C%20serious%20professional%20atmosphere%2C%20natural%20daylight%20through%20large%20windows%2C%20editorial%20photography%20with%20clean%20composition&width=1600&height=600&seq=rv-industria-cover&orientation=landscape',
    fechaVerificacion: '2026-01-10',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-VRF-00004',
    beneficiosActivos: ['Mayor Visibilidad', 'Prioridad Búsquedas'],
    posicionRanking: 21,
    enTendencia: false,
    prioridadBusqueda: 49,
    tags: ['noticias', 'chile', 'periodismo', 'investigación'],
  },
  {
    id: 22,
    nombre: 'Radio Comunitaria Unida',
    frecuencia: '87.02 RFW',
    categoriaRadio: 'Comunitaria',
    categoriaVerificacion: 'verificada',
    ciudad: 'Chicago',
    pais: 'Estados Unidos',
    locutorPrincipal: 'Colectivo Vecinal',
    oyentes: 1567,
    seguidores: 180000,
    rating: 4.35,
    estaEnVivo: true,
    descripcion: 'La voz de la comunidad hispana en Chicago. Cultura, eventos locales y participación ciudadana. Un espacio donde cada vecino tiene micrófono abierto.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20with%20people%20holding%20hands%20in%20circle%2C%20minimalist%20design%20with%20warm%20earth%20tones%2C%20green%20and%20orange%20colors%2C%20inclusive%20broadcast%20emblem%2C%20grassroots%20style&width=200&height=200&seq=rv-comunitaria&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Warm%20community%20radio%20studio%20in%20Chicago%20neighborhood%2C%20diverse%20team%20of%20volunteers%20around%20microphones%2C%20casual%20welcoming%20atmosphere%2C%20local%20art%20on%20walls%2C%20natural%20window%20light%2C%20grassroots%20authentic%20broadcast%20environment%2C%20documentary%20photography%20style&width=1600&height=600&seq=rv-comunitaria-cover&orientation=landscape',
    fechaVerificacion: '2026-02-20',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-VRF-00005',
    beneficiosActivos: ['Mayor Visibilidad'],
    posicionRanking: 22,
    enTendencia: false,
    prioridadBusqueda: 47,
    tags: ['comunitaria', 'chicago', 'hispana', 'local'],
  },
  {
    id: 23,
    nombre: 'Radio Voz del Pueblo',
    frecuencia: '160.00 RFW',
    categoriaRadio: 'Comunitaria',
    categoriaVerificacion: 'verificada',
    ciudad: 'Denver',
    pais: 'Estados Unidos',
    locutorPrincipal: 'Comunidad Activa',
    oyentes: 2890,
    seguidores: 210000,
    rating: 4.28,
    estaEnVivo: true,
    descripcion: 'La plataforma para que tu voz sea escuchada. Programación hecha por y para la gente de Denver. Contenido local, debates comunitarios y música elegida por los oyentes.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Local%20community%20radio%20station%20logo%20with%20city%20skyline%20and%20microphone%2C%20circular%20design%20warm%20brown%20and%20cream%20colors%2C%20grassroots%20broadcast%20emblem%2C%20local%20charm%2C%20Denver%20Colorado%20style&width=200&height=200&seq=rv-vozpueblo&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20studio%20in%20Denver%20with%20mountain%20view%20through%20windows%2C%20casual%20volunteer%20broadcasters%2C%20local%20art%20and%20community%20board%20on%20walls%2C%20warm%20natural%20light%2C%20authentic%20grassroots%20atmosphere%2C%20documentary%20style%20photography&width=1600&height=600&seq=rv-vozpueblo-cover&orientation=landscape',
    fechaVerificacion: '2026-03-05',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-VRF-00006',
    beneficiosActivos: ['Mayor Visibilidad'],
    posicionRanking: 23,
    enTendencia: false,
    prioridadBusqueda: 45,
    tags: ['comunitaria', 'denver', 'local', 'participativa'],
  },
  {
    id: 24,
    nombre: 'Radio Vida Nueva',
    frecuencia: '180.00 RFW',
    categoriaRadio: 'Cristiana',
    categoriaVerificacion: 'verificada',
    ciudad: 'Orlando',
    pais: 'Estados Unidos',
    locutorPrincipal: 'Hermano José Luis',
    oyentes: 4156,
    seguidores: 340000,
    rating: 4.60,
    estaEnVivo: true,
    descripcion: 'Música de adoración y mensajes inspiradores para toda la familia cristiana. El Hermano José Luis lidera una comunidad de fe que trasciende fronteras.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Christian%20worship%20radio%20station%20logo%20with%20cross%20and%20sunrise%20symbols%2C%20circular%20design%20soft%20blue%20and%20white%20colors%2C%20peaceful%20spiritual%20broadcast%20emblem%2C%20minimalist%20style&width=200&height=200&seq=rv-vidanueva&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Peaceful%20Christian%20radio%20studio%20in%20Orlando%20Florida%20with%20soft%20morning%20light%2C%20simple%20elegant%20wooden%20desk%2C%20inspirational%20wall%20art%20with%20Bible%20verses%2C%20calm%20spiritual%20atmosphere%2C%20professional%20broadcast%20equipment%2C%20editorial%20photography%20with%20serene%20natural%20light&width=1600&height=600&seq=rv-vidanueva-cover&orientation=landscape',
    fechaVerificacion: '2026-04-10',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-VRF-00007',
    beneficiosActivos: ['Mayor Visibilidad'],
    posicionRanking: 24,
    enTendencia: false,
    prioridadBusqueda: 43,
    tags: ['cristiana', 'orlando', 'adoración', 'fe'],
  },
  {
    id: 25,
    nombre: 'Radio Peninsular',
    frecuencia: '101.90 RFW',
    categoriaRadio: 'Clásica',
    categoriaVerificacion: 'verificada',
    ciudad: 'Caracas',
    pais: 'Venezuela',
    locutorPrincipal: 'Hugo Cárdenas',
    oyentes: 3400,
    seguidores: 290000,
    rating: 4.50,
    estaEnVivo: false,
    descripcion: 'Trayectoria comprobada de más de 35 años, legado cultural y audiencia multigeneracional fiel. Hugo Cárdenas es una leyenda viva de la radio venezolana.',
    logoUrl: 'https://readdy.ai/api/search-image?query=Nostalgic%20classic%20radio%20station%20logo%20design%2C%20vintage%20elegant%20broadcast%20identity%2C%20warm%20sepia%20and%20copper%20tones%2C%20retro%20circular%20emblem%20with%20frequency%2C%20vinyl-inspired%20geometric%20motif%2C%20premium%20brand%20identity%20design&width=200&height=200&seq=rv-peninsular&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Vintage%20classic%20radio%20studio%20in%20Caracas%20with%20warm%20nostalgic%20lighting%2C%20antique%20radio%20equipment%20on%20display%2C%20wooden%20bookshelves%20filled%20with%20vinyl%20records%2C%20experienced%20broadcaster%20at%20classic%20microphone%2C%20timeless%20atmosphere%2C%20editorial%20photography%20with%20warm%20amber%20tones&width=1600&height=600&seq=rv-peninsular-cover&orientation=landscape',
    fechaVerificacion: '2026-05-20',
    otorgadoPor: 'Andrés Calle',
    numeroInsignia: 'RFW-VRF-00008',
    beneficiosActivos: ['Mayor Visibilidad'],
    posicionRanking: 25,
    enTendencia: false,
    prioridadBusqueda: 41,
    tags: ['clásica', 'venezuela', 'legado', 'nostalgia'],
  },
];

export function getRadiosPorCategoria(categoria: CategoriaRadioVerificada | 'todas'): RadioVerificada[] {
  if (categoria === 'todas') return radiosVerificadas;
  return radiosVerificadas.filter(r => r.categoriaVerificacion === categoria);
}

export function getTopRadiosVerificadas(n: number = 10): RadioVerificada[] {
  return radiosVerificadas.sort((a, b) => a.posicionRanking - b.posicionRanking).slice(0, n);
}

export function getRadiosEnTendencia(): RadioVerificada[] {
  return radiosVerificadas.filter(r => r.enTendencia);
}

export function getRadiosEnVivo(): RadioVerificada[] {
  return radiosVerificadas.filter(r => r.estaEnVivo);
}

export function getRadiosPorBeneficio(beneficioId: number): RadioVerificada[] {
  const beneficioTitulo = beneficiosVerificacion.find(b => b.id === beneficioId)?.titulo || '';
  return radiosVerificadas.filter(r => r.beneficiosActivos.includes(beneficioTitulo));
}

export function formatearNumero(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}